import { pageTitle } from '@palaccount/config';
import type { JournalStatus } from '@palaccount/contracts';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AccountingTabs } from '@/features/accounts/accounting-tabs';
import { usePagedState, usePageRange } from '@/lib/paging';
import { cn } from '@/lib/utils';

import { useJournals } from './api';
import { JournalSourceCell } from './journal-source';

const STATUSES: JournalStatus[] = [
  'draft',
  'pending_approval',
  'approved',
  'rejected',
  'posted',
  'reversed',
];

/**
 * FIN-02 §7 chip colours: draft grey, pending amber, posted green, rejected
 * red, reversed blue.
 *
 * Approved is drawn a shade SPENT of Posted — a journal that has cleared
 * review but has not yet reached the ledger. That fade used to be `opacity-80`
 * on the whole chip, which is the SEVENTH instance of the defect written up on
 * `components/status-chip.tsx`: a group opacity composites the FILL and the
 * INK toward the surface together, so the two close on each other and the word
 * you are meant to read fades along with the colour you are reading it on.
 *
 * Measured in light theme it was 4.27:1 against the NFR-39 bar of 4.5 — and
 * 2.96:1 read in linear light, so unlike the pairs pinned in
 * `theme-contrast.test.ts` it failed under BOTH compositing models and was not
 * an artefact of which convention you measure in. Dark theme cleared the bar
 * either way (6.97 sRGB, 8.56 linear): the defect was LIGHT-ONLY, which is how
 * it survived six sweeps of its siblings — a glance in dark theme absolves it.
 *
 * `bg-emerald-100/80` paints the IDENTICAL fill pixel — same colour, same 80%
 * — and leaves the ink at full strength: 6.84:1 sRGB and 6.86:1 linear. NO
 * COLOUR VALUE MOVED and none may; decision 20 is open and the palette belongs
 * to the owner. Guarded by `src/test/faded-chip-contrast.test.ts`, which reads
 * this map directly.
 *
 * NOTE, load-bearing, and please do not undo it while tidying. That guard
 * harvests chips by matching SINGLE-QUOTED spans that name a background
 * utility, then keeping the ones that also name an ink utility. THIS COMMENT
 * SITS INSIDE ONE OF THOSE SPANS — every comment between the close of one
 * string literal and the open of the next does — so the fill classes quoted
 * above are already inside a candidate chip. It is filtered out only because
 * no ink-utility prefix is written anywhere in this comment, which is why the
 * paragraphs above say "the ink" in words and never spell the utility.
 *
 * Spell it, and this comment is harvested AS a chip: it carries a fill alpha
 * and a group alpha, so it measures as faded, and — the part that bites — the
 * span swallows the opening quote of the Draft entry below, removing a REAL
 * chip from the sweep. A phantom that happens to measure unfaded would hide a
 * real faded chip with no count moving at all. See
 * `src/test/faded-chip-contrast.test.ts`.
 */
const STATUS_CLASSES: Record<JournalStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  pending_approval: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  approved: 'bg-emerald-100/80 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  posted: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  reversed: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
};

export function JournalStatusChip({ status }: { status: JournalStatus }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border border-transparent px-2.5 py-0.5 text-xs font-semibold',
        STATUS_CLASSES[status],
      )}
    >
      {t(`je.status.${status}`)}
    </span>
  );
}

/**
 * FIN-02's register: number, date, reference, totals and the lifecycle chip,
 * newest first, with the period/status filters.
 */
export function JournalsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [status, setStatus] = useState<'' | JournalStatus>('');
  /** NFR-42 — the page rides in the URL, and every filter resets it. */
  const paging = usePagedState();

  /**
   * THE PERIOD RIDES IN THE URL, so this register can be LINKED TO.
   *
   * It used to be `useState('')`, which made the screen correct on its own
   * and unaddressable from outside: `/accounting/journals?from=…&to=…` opened
   * the whole register since the company's first entry, with the date boxes
   * blank. That is the shape `BR-DSH-01-08` and PART 14 §14.9 row 15 needed
   * and did not have — DSH-01's **view all** must open "Journal Entry (`FIN-02`),
   * filtered to the LAST 30 DAYS", and a link that lands on an unfiltered
   * register satisfies the rule's own sentence while dropping the half of
   * the destination that says which lines.
   *
   * One source of truth, deliberately: the parameters ARE the filter, not a
   * seed copied into state on mount. A seed can be edited into disagreeing
   * with the URL that produced it, and then a reload silently reverts the
   * reader's window — the half-state this project keeps catching. Absent
   * means unfiltered, so the canonical link to the register is still
   * `/accounting/journals` with no query string.
   *
   * `page` is deleted on every change, which is `usePagedState`'s property 3
   * (BR-DSH-09-19: any filter change returns to page 1) done inside the same
   * single write, rather than as a second `setParams` that could race it.
   * `status` stays in component state: nothing links to a status.
   */
  const [params, setParams] = useSearchParams();
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const setRange = (key: 'from' | 'to') => (value: string) => {
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (value) updated.set(key, value);
        else updated.delete(key);
        updated.delete('page');
        return updated;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    document.title = pageTitle(t('je.title'));
  }, [t]);

  const { data, isPending } = useJournals(
    {
      status: status || undefined,
      from: from || undefined,
      to: to || undefined,
    },
    paging.page,
    paging.pageSize,
  );
  const entries = data?.entries;
  const range = usePageRange(data, paging.pageSize);

  return (
    <div className="grid gap-4">
      <AccountingTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('je.title')}</h2>
        <Label htmlFor="je-from" className="text-xs">
          {t('coa.periodFrom')}
        </Label>
        <Input
          id="je-from"
          type="date"
          className="h-9 w-36"
          value={from}
          onChange={(e) => setRange('from')(e.target.value)}
        />
        <Label htmlFor="je-to" className="text-xs">
          {t('coa.periodTo')}
        </Label>
        <Input
          id="je-to"
          type="date"
          className="h-9 w-36"
          value={to}
          onChange={(e) => setRange('to')(e.target.value)}
        />
        <NativeSelect
          aria-label={t('je.filterStatus')}
          className="h-9 w-44"
          value={status}
          onChange={(e) => paging.change(setStatus)(e.target.value as '' | JournalStatus)}
        >
          <option value="">{t('sales.filterStatusAll')}</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`je.status.${value}`)}
            </option>
          ))}
        </NativeSelect>
        <Gated permission="accounting.create" reasonKey="perm.cannot.createJournals">
          <Button size="sm" asChild>
            <Link to="/accounting/journals/new">
              <Plus className="me-1 size-4" aria-hidden />
              {t('common.new')}
            </Link>
          </Button>
        </Gated>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('je.colNo')}</TableHead>
                <TableHead>{t('common.date')}</TableHead>
                <TableHead>{t('je.colReference')}</TableHead>
                {/* A15 / BR-FIN-02-12 — the register's own Source column,
                    reusing the editor's field label rather than minting a
                    second word for one thing. It sits after Reference because
                    that is where FIN-02 §5a puts it among the header fields,
                    and before the money, which the eye reads as a block. */}
                <TableHead>{t('je.fSource')}</TableHead>
                <TableHead className="text-end">{t('je.colDebit')}</TableHead>
                <TableHead className="text-end">{t('je.colCredit')}</TableHead>
                <TableHead>{t('je.colStatus')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(entries ?? []).map((entry) => (
                <TableRow
                  key={entry.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/accounting/journals/${entry.id}`)}
                >
                  <TableCell>
                    <Ltr value={entry.code} className="font-mono text-muted-foreground" />
                  </TableCell>
                  <TableCell>
                    <Ltr value={entry.entryDate} className="font-mono" />
                  </TableCell>
                  <TableCell className="max-w-64 truncate font-medium">
                    {entry.reference ?? '—'}
                  </TableCell>
                  {/* The row navigates to the entry on click; a source LINK
                      inside it navigates somewhere else. `stopPropagation` is
                      what keeps the two from firing together — without it a
                      click on the source would open the source and then be
                      overtaken by the row's own navigation to the entry, which
                      reads as a link that does not work. */}
                  <TableCell
                    className="max-w-56 truncate"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <JournalSourceCell source={entry.source} autoGenerated={entry.autoGenerated} />
                  </TableCell>
                  <TableCell className="text-end">
                    <Amount value={entry.totalDebit} />
                  </TableCell>
                  <TableCell className="text-end">
                    <Amount value={entry.totalCredit} />
                  </TableCell>
                  <TableCell>
                    <JournalStatusChip status={entry.status} />
                  </TableCell>
                </TableRow>
              ))}
              {!isPending && (entries ?? []).length === 0 ? (
                <TableRow>
                  {/* Seven since A15 added Source — an empty-state cell that
                      does not span the whole table leaves a stray column. */}
                  <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                    {t('je.empty')}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          {/* NFR-42 — the range, the total, and the two steps. */}
          {!isPending && (data?.total ?? 0) > 0 ? (
            <div className="p-4">
              <Pager
                page={paging.page}
                pages={range.pages}
                first={range.first}
                last={range.last}
                total={range.total}
                onPage={paging.setPage}
                label={t('je.title')}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
